/**
 * PageHero — reusable hero header for every menu page.
 *
 * Reads from site_settings:
 *   page_{slug}_hero_image
 *   page_{slug}_hero_overline
 *   page_{slug}_hero_title
 *   page_{slug}_hero_subtitle
 *
 * Falls back to props if a setting is missing.
 *
 * Props:
 *   slug:        page slug (e.g. "plants", "identify", "tours", "season", "recipes", "quiz", "compare", "journal", "magazin", "contact")
 *   defaultImage / defaultOverline / defaultTitle / defaultSubtitle (optional fallbacks)
 *   variant:     "image" (default, photo BG + olive gradient overlay) | "soft" (cream BG + botanical SVG, like /magazin header)
 */
import HeartDivider from "./HeartDivider";
import { useSettings } from "../lib/settings";

export default function PageHero({
  slug,
  defaultImage = "",
  defaultOverline = "",
  defaultTitle = "",
  defaultSubtitle = "",
  variant = "image",
  imageAlt = "",
  imagePosition = "center",
}) {
  const s = useSettings() || {};
  const image = s[`page_${slug}_hero_image`] || defaultImage;
  const overline = s[`page_${slug}_hero_overline`] || defaultOverline;
  const title = s[`page_${slug}_hero_title`] || defaultTitle;
  const subtitle = s[`page_${slug}_hero_subtitle`] || defaultSubtitle;

  if (variant === "soft") {
    return (
      <section
        className="relative py-16 md:py-24 overflow-hidden border-b"
        style={{ backgroundColor: "var(--wp-cream)", borderColor: "var(--wp-border)" }}
        data-testid={`pagehero-${slug}`}
      >
        <svg className="absolute -left-12 top-1/2 -translate-y-1/2 opacity-[0.12] pointer-events-none hidden md:block" width="220" height="320" viewBox="0 0 220 320" fill="none" aria-hidden="true">
          <g stroke="var(--wp-olive)" strokeWidth="1.2" fill="none">
            <path d="M100 0 L100 320" />
            {[40,90,150,215,280].map((y, i) => (
              <g key={i}>
                <path d={`M100 ${y} q -25 -10 -45 -25`} />
                <circle cx="55" cy={y-25} r="3" fill="var(--wp-olive)" />
                <circle cx="48" cy={y-30} r="2" fill="var(--wp-olive)" />
                <circle cx="62" cy={y-18} r="2" fill="var(--wp-olive)" />
              </g>
            ))}
          </g>
        </svg>
        <svg className="absolute -right-12 top-1/2 -translate-y-1/2 opacity-[0.12] pointer-events-none hidden md:block" width="220" height="320" viewBox="0 0 220 320" fill="none" aria-hidden="true" style={{ transform: "translateY(-50%) scaleX(-1)" }}>
          <g stroke="var(--wp-olive)" strokeWidth="1.2" fill="none">
            <path d="M100 0 L100 320" />
            {[40,90,150,215,280].map((y, i) => (
              <g key={i}>
                <path d={`M100 ${y} q -25 -10 -45 -25`} />
                <circle cx="55" cy={y-25} r="3" fill="var(--wp-olive)" />
                <circle cx="48" cy={y-30} r="2" fill="var(--wp-olive)" />
                <circle cx="62" cy={y-18} r="2" fill="var(--wp-olive)" />
              </g>
            ))}
          </g>
        </svg>
        <div className="max-w-3xl mx-auto px-6 text-center relative z-10">
          {overline && <div className="font-modern uppercase mb-4" style={{ letterSpacing: "0.32em", fontSize: "0.75rem", color: "var(--wp-olive)", opacity: 0.7 }}>{overline}</div>}
          <h1 className="font-display leading-[1.05] mb-6" style={{ color: "var(--wp-olive)", fontSize: "clamp(2.5rem, 6vw, 4.5rem)", fontWeight: 500 }}>
            {title}
          </h1>
          {subtitle && <p className="font-body text-base sm:text-lg max-w-xl mx-auto leading-relaxed mb-8" style={{ color: "var(--wp-forest)", opacity: 0.85 }}>{subtitle}</p>}
          <div className="flex justify-center"><HeartDivider width="medium" tone="olive" /></div>
        </div>
      </section>
    );
  }

  // image variant – full-width photo with olive gradient overlay
  return (
    <section className="relative w-full" data-testid={`pagehero-${slug}`}>
      <div className="relative h-[48vh] min-h-[340px] w-full overflow-hidden">
        {image && <img src={image} alt={imageAlt} className="absolute inset-0 w-full h-full object-cover" style={{ objectPosition: imagePosition }} data-testid={`pagehero-${slug}-image`} />}
        <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(58,57,44,0.82) 0%, rgba(58,57,44,0.4) 55%, rgba(58,57,44,0.12) 100%)" }} />
        <div className="relative z-10 h-full max-w-7xl mx-auto px-6 md:px-8 lg:px-12 flex items-end pb-12 md:pb-16">
          <div className="max-w-2xl">
            {overline && <div className="overline mb-3" style={{ color: "#E8D7B5" }}>{overline}</div>}
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[var(--wp-cream)] leading-[1.05] tracking-tight">
              {title}
            </h1>
            {subtitle && <p className="mt-4 text-base md:text-lg leading-relaxed max-w-xl" style={{ color: "rgba(245,240,232,0.92)" }}>{subtitle}</p>}
          </div>
        </div>
      </div>
    </section>
  );
}
